import ContactNameFields from '../shared/ContactNameFields';
import PhoneChannelBadges from '../communications/PhoneChannelBadges';
import { usePhoneCapability } from '../../lib/phoneChannelApi';
import '../../styles/restScreens.css';
import DocumentsPanel from '../documents/DocumentsPanel';
import AccountPaymentsPanel from '../payments/AccountPaymentsPanel';
import { useId, useState, useEffect, useCallback } from 'react';
import { supabaseClient } from '../../config/supabase';
import { Photo } from './ClientHybridCard';
import PhotoEnrichmentPanel from './PhotoEnrichmentPanel';
import ResizableDisclosure from '../shared/ResizableDisclosure';
import StaffCardEntryPanel from '../payments/StaffCardEntryPanel';
import ChargeInvoiceAction from '../invoicing/ChargeInvoiceAction';
import ContactBillingPreferences from './ContactBillingPreferences';
import ClientOnboardingChecklist from './ClientOnboardingChecklist';
import ClientSetupPanel from './ClientSetupPanel';
import CrmActivityTimeline from './CrmActivityTimeline';
import ClientPortalAccessPanel from './ClientPortalAccessPanel';
import { ApprovedVendorsManager } from '../events/foundation/VendorPicker';
import { formatPhone } from '../../lib/phone';
import LabeledMultiField from '../shared/LabeledMultiField';
import ReachedVia from './ReachedVia';
import ContactTimeZoneField, { ContactTimeZoneText } from './ContactTimeZoneField';
import { resolveContactTimeZone } from '../../lib/contactTimeZone';
import { clientDisclosureConfig, accountKindOf } from './clientDisclosureConfig';
import { entriesOf, methodLabelText, primaryEntry } from '../../lib/contactMethods';
import {
    ACCOUNT_STATUS_META, statusMeta, TONE_CLASS, tierTone, toneTextStyle,
    ACCOUNT_FLAGS, firstEmail, firstPhone, hasHumanReview,
} from './clientsConstants';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import {
    RecordCardActionButton,
    RecordCardDeleteChoices,
    RecordCardProfileTabs,
} from '../shared/RecordCardControls';
import useDisclosureCardClick from '../shared/useDisclosureCardClick';
import ContactCommunications from './ContactCommunications';
import { consentSourceLabel } from '../../lib/smsConsent';
import ResetTestContactDialog from '../ai/ResetTestContactDialog';
import { canResetTestContact } from '../../lib/testContactReset';

import { BTN, standardPillClass } from '../ui/platformStandard';
import ClickToCallButton from '../communications/ClickToCallButton';
// Module 2 — Tier 3: the full "Peacock" account card (UX Addendum §3/§4).
//
// The canvas, ink, inputs and borders follow the active global theme tokens. The signature
// teal-to-green band is the card's identity and stays constant across themes.
//
// Opens IN PLACE from the Tier-2 peek. Never a modal (WR-013). Edit flips the whole card
// editable and becomes Save — whole-card edit for v1 (Gate 5).
//
// TABS — all seven visible (Val, 2026-07-15: "Visible please, so I know what belongs and/or
// what's coming"). Overview and Sub-Accounts have data. The other five do not, and each says
// what it is waiting for instead of showing an empty box.
//
// Naming an empty tab's blocker is the difference between a roadmap and a lie. A tab that
// renders nothing implies the data is missing; a tab that says "Reservations appear here once
// a deal converts" tells you the system is fine and the work hasn't happened yet. This
// codebase already has three features that were built, declared done, and wired to nothing —
// these are the opposite: not built, and saying so.
//
// `Bookings` wears the TENANT's word: The Ride calls a job a Reservation. The label comes from
// the tenant; the code branches on the key. Same rule as everywhere else in Module 2.
const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'subs',     label: 'Sub-Accounts' },
    { key: 'jobs',     label: 'Reservations',
      waiting: 'Reservations appear here once this tab is wired to query engagements.account_id. The field is now the canonical client link (BL-181 Gate 2 — FK-backed, captured on the Engagement form) and backfilled from the old booker_id column; only the read side here is still unbuilt.' },
    // Billing is no longer an empty tab: BL-173 Gate 3c mounts the staff manual card-entry
    // panel here. Invoices/payments/statements are still Module 6's, and the tab says so.
    { key: 'billing',  label: 'Billing' },
    { key: 'docs', label: 'Documents' },
    { key: 'payments', label: 'Payments' },
    { key: 'notes',    label: 'Timeline' },
    { key: 'comms',    label: 'Comms' },
];


/** How many of these contacts (primary account = this one) would have no account left once it is deleted. */
export function countContactsOrphanedByDelete(contacts = [], accountId) {
    return contacts.filter(c => !(Array.isArray(c?.contact_accounts)
        && c.contact_accounts.some(link => link?.account_id && link.account_id !== accountId))).length;
}

export function deleteChoicesMessage(orphanedCount) {
    const base = 'This will remove this client from active views.';
    if (!orphanedCount) return base;
    const people = orphanedCount === 1 ? '1 contact' : `${orphanedCount} contacts`;
    return `${base} Deleting it permanently leaves ${people} without an account — ${orphanedCount === 1 ? 'that contact is' : 'they are'} kept, and shown under "Contacts without an account".`;
}

function Chip({ tone, children }) {
    return (
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${TONE_CLASS[tone] || TONE_CLASS.muted}`} style={toneTextStyle(tone)}>
            {children}
        </span>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">{label}</div>
            <div className="text-sm text-[var(--ink)]">{children}</div>
        </div>
    );
}

const inputCls = 'w-full rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-2 py-1.5 text-sm text-[var(--ink)] placeholder:text-[var(--ink-muted)] ' +
                 'outline-none focus:border-brand-500';

// 1.12.212-beta: a phone's channel badges on the contact card (cached answer only — the card never pays for a lookup
// by itself; "Check now" does, and a number is checked automatically when it is added or first used in a conversation).
function PhoneRowChannels({ phone }) {
    const state = usePhoneCapability(supabaseClient, phone, { autoCheck: false });
    if (!state.phone) return null;
    return (
        <PhoneChannelBadges
            capability={state.capability}
            checking={state.checking}
            notice={state.notice}
            onCheckNow={state.canCheckNow ? state.checkNow : undefined}
            onTryWhatsAppAgain={state.tryWhatsAppAgain}
        />
    );
}

function ClientFullCard({ account, tiers = [], drivers = [], onCollapse, onCollapseAll, onSaved, editor, canEdit = true, canViewFinancial = true }) {
    const contact = account.primary_contact;
    const meta = statusMeta(account.status);

    const [tab, setTab] = useState('overview');
    const [profileTab, setProfileTab] = useState('overview');
    const [settingsSection, setSettingsSection] = useState('communication');
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [destructiveAction, setDestructiveAction] = useState(null);
    const [showDeleteChoices, setShowDeleteChoices] = useState(false);
    // Comms next pass (5d): before a permanent delete, say how many contacts would be left with no account. Nothing is
    // deleted with the account (contacts.primary_account_id is ON DELETE SET NULL); they show under "Contacts without an account".
    const [orphanedOnDelete, setOrphanedOnDelete] = useState(null);
    useEffect(() => {
        if (!showDeleteChoices || !account?.id) { setOrphanedOnDelete(null); return undefined; }
        let live = true;
        (async () => {
            try {
                const { data, error: countError } = await supabaseClient
                    .from('contacts')
                    .select('id, contact_accounts!contact_accounts_contact_id_fkey ( account_id )')
                    .eq('primary_account_id', account.id);
                if (!live) return;
                if (countError) { setOrphanedOnDelete(null); return; }
                setOrphanedOnDelete(countContactsOrphanedByDelete(data || [], account.id));
            } catch {
                if (live) setOrphanedOnDelete(null);
            }
        })();
        return () => { live = false; };
    }, [showDeleteChoices, account?.id]);
    const [error, setError] = useState(null);
    const [clientMenuOpen, setClientMenuOpen] = useState(false);
    const [resettableAddress, setResettableAddress] = useState(null);
    const [resetTestContactOpen, setResetTestContactOpen] = useState(false);

    const [subs, setSubs] = useState([]);
    const [subsLoaded, setSubsLoaded] = useState(false);
    const contentId = useId();
    const { onClick, onDoubleClick, onKeyDown, onPointerDownCapture, headerRef } = useDisclosureCardClick({
        isOpen: true,
        onClose: onCollapseAll,
        openedAt: editor?.openedAt,
    });

    const [form, setForm] = useState(() => clientDisclosureConfig.createDraft(account));
    // The person's other accounts (Business | Personal tabs): one tab per account, each showing that account and the person's role in it.
    const accountLinks = (contact?.contact_accounts || []).filter(link => link.account);
    const [accountTab, setAccountTab] = useState(account.id);
    const contactPhone = firstPhone(contact);
    const contactEmail = firstEmail(contact);

    useEffect(() => {
        let live = true;
        setResettableAddress(null);
        (async () => {
            for (const address of [contactPhone, contactEmail]) {
                if (address && await canResetTestContact(supabaseClient, address)) {
                    if (live) setResettableAddress(address);
                    return;
                }
            }
        })();
        return () => { live = false; };
    }, [contactPhone, contactEmail]);

    // Sub-accounts load when the tab is first opened — not on every card open.
    const loadSubs = useCallback(async () => {
        const { data, error: e } = await supabaseClient
            .from('sub_accounts')
            .select('id, client_code, sub_account_name, status, primary_contact_id')
            .eq('parent_account_id', account.id)
            .order('client_code');
        if (!e) setSubs(data || []);
        setSubsLoaded(true);
    }, [account.id]);

    useEffect(() => {
        if (tab === 'subs' && !subsLoaded) loadSubs();
    }, [tab, subsLoaded, loadSubs]);

    const save = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await clientDisclosureConfig.saveDraft(account, form);
            setEditing(false);
            await onSaved?.();
        } catch (err) {
            setError(err.message || String(err));
        } finally {
            setSaving(false);
        }
    }, [account, form, onSaved]);

    // ProgressiveDisclosure owns this state when the card is used in the CRM
    // cascade. Keeping the local fallback makes this card independently usable.
    const activeEditing = canEdit && (editor?.editing ?? editing);
    const activeForm = editor?.draft ?? form;
    const activeSaving = editor?.saving ?? saving;
    const activeError = editor?.error ?? error;
    const updateForm = useCallback((key, value) => {
        if (editor) editor.updateDraft(key, value);
        else setForm(current => ({ ...current, [key]: value }));
    }, [editor]);
    const startEditing = useCallback(() => {
        if (editor) editor.startEditing();
        else setEditing(true);
    }, [editor]);
    const cancelEditing = useCallback(() => {
        setShowDeleteChoices(false);
        if (editor) editor.cancelEditing();
        else {
            setEditing(false);
            setError(null);
        }
    }, [editor]);
    const saveCard = useCallback(() => {
        if (editor) return editor.save();
        return save();
    }, [editor, save]);

    const archiveAccount = async () => {
        setDestructiveAction('archive');
        setError(null);
        try {
            if (!account.org_id) throw new Error('Unable to archive client: missing org scope.');
            const archiveQuery = supabaseClient
                .from('accounts')
                .update({ status: 'archived' })
                .eq('id', account.id)
                .eq('org_id', account.org_id);
            const { error: aErr } = await archiveQuery;
            if (aErr) throw aErr;

            setShowDeleteChoices(false);
            setEditing(false);
            await onSaved?.();
            onCollapse?.();
        } catch (err) {
            setError(err.message || String(err));
        } finally {
            setDestructiveAction(null);
        }
    };

    const deleteAccount = async () => {
        setDestructiveAction('delete');
        setError(null);
        try {
            if (!account.org_id) throw new Error('Unable to delete client: missing org scope.');
            const deleteQuery = supabaseClient
                .from('accounts')
                .delete()
                .eq('id', account.id)
                .eq('org_id', account.org_id);
            const { error: aErr } = await deleteQuery;
            if (aErr) throw aErr;

            setShowDeleteChoices(false);
            setEditing(false);
            await onSaved?.();
            onCollapse?.();
        } catch (err) {
            setError(err.message || String(err));
        } finally {
            setDestructiveAction(null);
        }
    };

    const tier = tiers.find(t => t.id === (activeEditing ? activeForm.customer_tier_id : account.customer_tier_id));
    const draftPrimaryEmail = primaryEntry(activeForm.emails)?.value || '';
    const enrichmentContact = contact
        ? { ...contact, name: activeForm.contact_name, emails: draftPrimaryEmail ? [draftPrimaryEmail] : [], photo_url: activeForm.photo_url }
        : { name: activeForm.contact_name, emails: draftPrimaryEmail ? [draftPrimaryEmail] : [], photo_url: activeForm.photo_url };
    const enrichmentAccount = { ...account, account_name: activeForm.account_name };
    const preferredDrivers = Array.isArray(account.preferred_drivers) ? account.preferred_drivers : [];
    const driverLabel = (driverId) => {
        const driver = drivers.find(item => item.id === driverId);
        return driver ? `${driver.id.slice(0, 8)}… · ${driver.status || 'unknown status'}` : `${driverId.slice(0, 8)}…`;
    };

    return (
        <div data-m6-layout="clients" data-m6-component="ClientFullCard"
            role="group"
            tabIndex={0}
            className="peacock-card ia-depth-card overflow-hidden rounded-lg border border-[var(--surface-border)]"
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            onKeyDown={onKeyDown}
            onPointerDownCapture={onPointerDownCapture}
        >
            {/* The signature band: teal -> sheen -> green, straight from the locked palette. */}
            <div className="peacock-band h-1.5" />

            {/* Identity band — sticky, so who you're looking at never scrolls away. */}
            <div className="sticky top-0 z-10 border-b border-[var(--surface-border)] bg-[var(--surface-canvas)]/95 px-5 pb-3 pt-4 backdrop-blur">
                <div className="mb-2 flex items-start justify-between gap-2">
                    <button ref={headerRef} type="button" aria-expanded={true} aria-controls={contentId}
                        className="text-left font-semibold text-[var(--ink)]">
                        {contact?.name || account.account_name}
                    </button>
                    <div className="relative">
                        <button type="button" aria-label="Client actions" aria-expanded={clientMenuOpen}
                            className="rounded-md px-2 py-1 text-lg leading-none text-[var(--ink)] hover:bg-[var(--surface-sunken)]"
                            onClick={(event) => { event.stopPropagation(); setClientMenuOpen((current) => !current); }}>
                            ⋯
                        </button>
                        {clientMenuOpen && (
                            <div role="menu" className="absolute right-0 z-20 mt-1 min-w-48 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-canvas)] p-1 shadow-lg">
                                {resettableAddress && (
                                    <button type="button" role="menuitem"
                                        className="w-full rounded px-3 py-2 text-left text-sm text-[var(--ink)] hover:bg-[var(--surface-sunken)]"
                                        onClick={(event) => { event.stopPropagation(); setClientMenuOpen(false); setResetTestContactOpen(true); }}>
                                        Reset test contact
                                    </button>
                                )}
                                {!resettableAddress && <p className="px-3 py-2 text-xs text-[var(--ink-muted)]">No test-contact actions available.</p>}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex gap-4 items-start">
                    <Photo contact={contact ? { ...contact, photo_url: activeForm.photo_url ?? contact?.photo_url } : { photo_url: activeForm.photo_url }} size="lg" />
                    <div className="flex-1 min-w-0">
                        {activeEditing ? (
                            <div className="grid gap-2 sm:grid-cols-2 max-w-xl">
                                <ContactNameFields className={inputCls} firstName={activeForm.contact_first_name} lastName={activeForm.contact_last_name} value={activeForm.contact_name} onChange={(value, parts) => { updateForm('contact_name', value); updateForm('contact_first_name', parts.firstName); updateForm('contact_last_name', parts.lastName); }} />
                                <input className={inputCls} value={activeForm.position}
                                       onChange={e => updateForm('position', e.target.value)}
                                       placeholder="Position" />
                                <select className={inputCls} value={activeForm.account_kind} aria-label="Account type"
                                        onChange={e => updateForm('account_kind', e.target.value)}>
                                    <option value="business">Account type: Business</option>
                                    <option value="personal">Account type: Personal</option>
                                </select>
                                <input className={inputCls} value={activeForm.account_name}
                                       onChange={e => updateForm('account_name', e.target.value)}
                                       placeholder={activeForm.account_kind === 'personal' ? 'Last name (the account is named after the person)' : 'Company name'} />
                            </div>
                        ) : (
                            <>
                                <div className="truncate text-sm text-[var(--ink-muted)]">
                                    {contact?.position ? `${contact.position} · ` : ''}{account.account_name}
                                </div>
                            </>
                        )}
                        <div className="mt-1 font-mono text-xs text-[var(--ink-muted)]">
                            {account.account_number || 'No account number until this converts'}
                        </div>
                        {!activeEditing && contact?.id && <ClientPortalAccessPanel contactId={contact.id} contactName={contact.name || ''} />}
                        {!activeEditing && account?.id && <ApprovedVendorsManager client={supabaseClient} accountId={account.id} hideWhenUnavailable />}
                    </div>

                    <div className="flex flex-wrap justify-end gap-1.5 max-w-[45%]">
                        {tier && <Chip tone={tierTone(tier.family)}>{tier.name}</Chip>}
                        <Chip tone="muted">{accountKindOf(account) === 'personal' ? 'Personal' : 'Business'}</Chip>
                        <Chip tone={meta.tone}>{meta.label}</Chip>
                        {/* Every flag shows, on AND off (the render says "Senior · off"). A hidden
                            flag and a flag switched off look identical, so hiding the off ones means
                            the card can't tell you "not a senior" apart from "nobody has said". */}
                        {!activeEditing && ACCOUNT_FLAGS.map(f => {
                            const isFlagActive = typeof f.isActive === 'function'
                                ? f.isActive(account)
                                : Boolean(account?.[f.key]);
                            return isFlagActive
                                ? <Chip key={f.key} tone={f.tone}>{f.label}</Chip>
                                : <Chip key={f.key} tone="muted">{f.label} · off</Chip>;
                        })}
                    </div>
                </div>

                <RecordCardProfileTabs
                    activeTab={profileTab}
                    tabs={[
                        ['overview', 'Overview'],
                        ['activity', 'Activity'],
                        ['onboarding', 'Onboarding'],
                        ['settings', 'Settings'],
                    ]}
                    onChange={(key) => {
                            setProfileTab(key);
                            if (key === 'overview') setTab('overview');
                            if (key === 'activity' && tab === 'overview') setTab('jobs');
                    }}
                />
            </div>

            <div id={contentId} className="px-5 py-4">
                {profileTab === 'activity' && (
                    <div className="mb-4 flex flex-wrap gap-1 border-b border-[var(--surface-border)] pb-2" aria-label="Activity sections">
                        {TABS.filter((item) => !['overview', 'billing'].includes(item.key)).map((item) => (
                            <button
                                key={item.key}
                                onClick={() => setTab(item.key)}
                                title={item.waiting || undefined}
                                aria-pressed={tab === item.key}
                                style={{ minHeight: 44, border: tab === item.key ? '1px solid #22C55E' : '1px solid var(--surface-border)', borderRadius: 999 }}
                                className={`rounded px-3 py-1.5 text-xs ${tab === item.key ? 'bg-[var(--surface-sunken)] text-[var(--ink)]' : 'text-[var(--ink-muted)] hover:text-[var(--ink)]'}`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                )}
                {profileTab !== 'settings' && profileTab !== 'onboarding' && (tab === 'overview' ? (
                    <div className="grid gap-5 md:grid-cols-3">
                        {activeEditing && (
                            <div className="md:col-span-3">
                                <PhotoEnrichmentPanel
                                    contact={enrichmentContact}
                                    account={enrichmentAccount}
                                    currentPhotoUrl={activeForm.photo_url || ''}
                                    onConfirmPhoto={(url) => updateForm('photo_url', url)}
                                    onClearPhoto={() => updateForm('photo_url', '')}
                                    supabaseClient={supabaseClient}
                                />
                            </div>
                        )}
                        {accountLinks.length > 1 && (
                            <div className="md:col-span-3" data-testid="account-tabs">
                                <div role="tablist" aria-label="This person's accounts" className="flex flex-wrap gap-1 border-b border-[var(--surface-border)]">
                                    {accountLinks.map(link => {
                                        const kinds = accountLinks.map(l => accountKindOf(l.account));
                                        const kindUnique = kinds.filter(k => k === accountKindOf(link.account)).length === 1;
                                        const label = kindUnique ? (accountKindOf(link.account) === 'personal' ? 'Personal' : 'Business') : link.account.account_name;
                                        const selected = accountTab === link.account_id;
                                        return (
                                            <button key={link.account_id} type="button" role="tab" aria-selected={selected}
                                                onClick={event => { event.stopPropagation(); setAccountTab(link.account_id); }}
                                                className={standardPillClass(selected)}>
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                                {accountLinks.filter(link => link.account_id === accountTab).map(link => (
                                    <div key={link.account_id} role="tabpanel" className="grid gap-3 py-3 text-sm sm:grid-cols-4">
                                        <Field label="Account">{link.account.account_name}</Field>
                                        <Field label="Account type">{accountKindOf(link.account) === 'personal' ? 'Personal' : 'Business'}</Field>
                                        <Field label="Role there">{link.role || <span className="text-[var(--ink-muted)]">—</span>}</Field>
                                        <Field label="Account #">{link.account.account_number || <span className="text-[var(--ink-muted)]">Lead</span>}</Field>
                                    </div>
                                ))}
                            </div>
                        )}
                        {/* Reach */}
                        <div className="space-y-3">
                            <div className="text-xs font-semibold uppercase tracking-wide" style={toneTextStyle('system')}>Reach</div>
                            {activeEditing ? (
                                <>
                                    <Field label="Phones">
                                        <LabeledMultiField kind="phone" ariaLabel="Phone" entries={activeForm.phones}
                                            onChange={rows => updateForm('phones', rows)} />
                                    </Field>
                                    <Field label="Emails">
                                        <LabeledMultiField kind="email" ariaLabel="Email" entries={activeForm.emails}
                                            onChange={rows => updateForm('emails', rows)} />
                                    </Field>
                                    <Field label="Addresses">
                                        <LabeledMultiField kind="address" ariaLabel="Address" entries={activeForm.addresses}
                                            onChange={rows => updateForm('addresses', rows)} />
                                    </Field>
                                    <Field label="Time zone">
                                        <ContactTimeZoneField
                                            value={activeForm.time_zone} source={activeForm.time_zone_source}
                                            addresses={activeForm.addresses} phones={activeForm.phones}
                                            onChange={(next) => { updateForm('time_zone', next.time_zone); updateForm('time_zone_source', next.time_zone_source); }} />
                                    </Field>
                                </>
                            ) : (
                                <>
                                    {['phone', 'email', 'address'].map(kind => {
                                        const rows = entriesOf(contact, kind).filter(e => (e.value || '').trim());
                                        return (
                                            <Field key={kind} label={kind === 'phone' ? 'Phones' : kind === 'email' ? 'Emails' : 'Addresses'}>
                                                {rows.length === 0 ? <span className="text-[var(--ink-muted)]">—</span> : rows.map(row => (
                                                    <div key={row.id || row.value} className="flex flex-wrap items-baseline gap-x-2">
                                                        <span>{kind === 'phone' ? formatPhone(row.value) : row.value}</span>
                                                        <span className="text-[11px] text-[var(--ink-muted)]">{methodLabelText(row.label)}{row.is_primary && rows.length > 1 ? ' · Primary' : ''}</span>
                                                        {kind === 'phone' && <PhoneRowChannels phone={row.value} />}
                                                    </div>
                                                ))}
                                            </Field>
                                        );
                                    })}
                                    <Field label="Time zone"><ContactTimeZoneText timeZone={resolveContactTimeZone(contact)} /></Field>
                                    <div className="flex gap-2 pt-1">
                                        {firstPhone(contact) && (
                                            <ClickToCallButton client={supabaseClient} accountId={account.id}
                                               clientPhone={firstPhone(contact)} fallbackTel={firstPhone(contact)} className={BTN} />
                                        )}
                                        {firstEmail(contact) && (
                                            <a href={`mailto:${firstEmail(contact)}`}
                                               className="px-3 py-1 rounded text-xs font-medium border hover:bg-[var(--surface-sunken)]"
                                               style={toneTextStyle('system')}>Email</a>
                                        )}
                                    </div>
                                </>
                            )}
                            {contact?.id && <ReachedVia contact={contact} />}
                        </div>

                        {/* Standing */}
                        <div className="space-y-3">
                            <div className="text-xs font-semibold uppercase tracking-wide" style={toneTextStyle('system')}>Standing</div>
                            {activeEditing ? (
                                <>
                                    <Field label="Status">
                                        <select className={inputCls} value={activeForm.status}
                                                onChange={e => updateForm('status', e.target.value)}>
                                            {Object.entries(ACCOUNT_STATUS_META).map(([v, m]) => (
                                                <option key={v} value={v}>{m.label}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Tier">
                                        <select className={inputCls} value={activeForm.customer_tier_id}
                                                onChange={e => updateForm('customer_tier_id', e.target.value)}>
                                            <option value="">No tier</option>
                                            {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Category">
                                        <input className={inputCls} value={activeForm.account_type}
                                               onChange={e => updateForm('account_type', e.target.value)} />
                                    </Field>
                                    <Field label="Preferred drivers (up to 3)">
                                        <select
                                            multiple
                                            aria-label="Preferred drivers"
                                            className={`${inputCls} min-h-24`}
                                            value={activeForm.preferred_drivers}
                                            onChange={e => updateForm(
                                                'preferred_drivers',
                                                [...e.target.selectedOptions].map(option => option.value).slice(0, 3)
                                            )}
                                        >
                                            {drivers.map(driver => (
                                                <option key={driver.id} value={driver.id}>{driverLabel(driver.id)}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <div className="space-y-1.5 pt-1">
                                        {ACCOUNT_FLAGS.map(f => (
                                            <label key={f.key} className="flex items-center gap-2 text-xs text-[var(--ink)]">
                                                <input type="checkbox" checked={activeForm[f.key]}
                                                       onChange={e => updateForm(f.key, e.target.checked)} />
                                                {f.label}
                                            </label>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Field label="Validated referrals">
                                        {account.validated_referral_count}
                                        <span className="ml-1 text-xs text-[var(--ink-muted)]">(from the ledger — not typed by hand)</span>
                                    </Field>
                                    <Field label="Account type">{accountKindOf(account) === 'personal' ? 'Personal' : 'Business'}</Field>
                                    <Field label="Category">{account.account_type || <span className="text-[var(--ink-muted)]">—</span>}</Field>
                                    <Field label="Agreement">
                                        {account.agreement_ref
                                            ? <span className="font-mono text-xs">{account.agreement_ref}</span>
                                            : <span className="text-[var(--ink-muted)]">None on file</span>}
                                    </Field>
                                    <Field label="Preferred drivers">
                                        {preferredDrivers.length
                                            ? preferredDrivers.map(driverLabel).join(', ')
                                            : <span className="text-[var(--ink-muted)]">None selected</span>}
                                    </Field>
                                </>
                            )}
                        </div>

                        {/* Preserve the existing column and keep its text visible while the
                            append-only timeline becomes the place for new activity. */}
                        <ResizableDisclosure
                            label="Original contact note (retained)"
                            labelStyle={toneTextStyle('system')}
                        >
                            <div className="whitespace-pre-wrap text-sm text-[var(--ink)]">
                                {contact?.notes || <span className="text-[var(--ink-muted)]">No original note on file.</span>}
                            </div>
                        </ResizableDisclosure>
                    </div>
                ) : tab === 'subs' ? (
                    <SubAccountsTab subs={subs} loaded={subsLoaded} />
                ) : tab === 'payments' ? (
                    <AccountPaymentsPanel key={account.id} client={supabaseClient} accountId={account.id} />
                ) : tab === 'docs' ? (
                    <DocumentsPanel key={account.id} client={supabaseClient} accountId={account.id} staff canEdit={canEdit} />
                ) : tab === 'notes' ? (
                    <CrmActivityTimeline account={account} contact={contact} />
                ) : tab === 'comms' ? (
                    <CrmActivityTimeline account={account} contact={contact} communicationsOnly />
                ) : (
                    <ComingTab tab={TABS.find(t => t.key === tab)} />
                ))}
                {profileTab === 'onboarding' && (
                    <ClientOnboardingChecklist account={account} />
                )}
                {profileTab === 'settings' && (
                    <div className="grid gap-5 md:grid-cols-[180px_minmax(0,1fr)]">
                        <nav aria-label="Profile settings" className="space-y-4">
                            <div>
                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-muted)]">Preferences</p>
                                {[
                                    ['communication', 'Communications'],
                                    ['rides', 'Rides'],
                                    ...(canViewFinancial ? [['billing', 'Billing & payment']] : []),
                                ].map(([key, label]) => (
                                    <button key={key} onClick={() => setSettingsSection(key)}
                                        className={`block w-full rounded px-2 py-1.5 text-left text-xs ${settingsSection === key ? 'bg-[var(--surface-sunken)] text-[var(--ink)]' : 'text-[var(--ink-muted)] hover:text-[var(--ink)]'}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <div>
                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-muted)]">Account</p>
                                {[
                                    ['contact', 'Contact details'],
                                    ['security', 'Sign-in & security'],
                                ].map(([key, label]) => (
                                    <button key={key} onClick={() => setSettingsSection(key)}
                                        className={`block w-full rounded px-2 py-1.5 text-left text-xs ${settingsSection === key ? 'bg-[var(--surface-sunken)] text-[var(--ink)]' : 'text-[var(--ink-muted)] hover:text-[var(--ink)]'}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </nav>
                        <div>
                            {settingsSection === 'communication'
                                ? (contact?.id
                                    ? <><SmsConsentProfile contact={contact} onSaved={onSaved} /><ClientSetupPanel contact={contact} /></>
                                    : <p className="text-sm text-[var(--ink-muted)]">Add a primary contact to manage communication preferences.</p>)
                                : settingsSection === 'billing'
                                    ? (canViewFinancial
                                        ? <BillingTab account={account} contact={contact} />
                                        : <p className="text-sm text-[var(--ink-muted)]">Financial access is not available.</p>)
                                    : settingsSection === 'contact'
                                        ? <div className="space-y-3"><Field label="Contact">{contact?.name || '—'}</Field><Field label="Email">{firstEmail(contact) || '—'}</Field><Field label="Mobile">{formatPhone(firstPhone(contact)) || '—'}</Field><Field label="Time zone"><ContactTimeZoneText timeZone={resolveContactTimeZone(contact)} /></Field></div>
                                        : <ComingTab tab={{
                                            label: settingsSection === 'rides' ? 'Rides' : 'Sign-in & security',
                                            waiting: settingsSection === 'rides'
                                                ? 'Ride preferences will appear here when customer ride settings are available.'
                                                : 'Sign-in and security settings are managed by the customer account portal, which is not present in this repository.',
                                        }} />}
                        </div>
                    </div>
                )}

                {activeError && (
                    <div className="mt-4 p-2 rounded bg-[var(--flow-orange)]/15 border border-[var(--flow-orange)] text-[var(--flow-orange)] text-sm">
                        {activeError}
                    </div>
                )}
                {resetTestContactOpen && resettableAddress && (
                    <ResetTestContactDialog
                        address={resettableAddress}
                        onDone={() => onSaved?.()}
                        onClose={() => setResetTestContactOpen(false)}
                    />
                )}

                {activeEditing && showDeleteChoices && (
                    <RecordCardDeleteChoices
                        message={deleteChoicesMessage(orphanedOnDelete)}
                        archiveBusy={destructiveAction === 'archive'}
                        deleteBusy={destructiveAction === 'delete'}
                        disabled={!!destructiveAction}
                        onArchive={archiveAccount}
                        onDelete={deleteAccount}
                        onCancel={() => setShowDeleteChoices(false)}
                    />
                )}

                {/* Centered card actions. */}
                <div className="mt-6 flex justify-center gap-3">
                    {activeEditing ? (
                        <>
                            <RecordCardActionButton onClick={saveCard} disabled={activeSaving || !!destructiveAction || showDeleteChoices}>
                                {activeSaving ? 'Saving…' : 'Save'}
                            </RecordCardActionButton>
                            <RecordCardActionButton
                                onClick={() => {
                                    setShowDeleteChoices(true);
                                    setError(null);
                                }}
                                disabled={activeSaving || !!destructiveAction || showDeleteChoices}
                            >
                                Delete
                            </RecordCardActionButton>
                            <RecordCardActionButton
                                onClick={() => {
                                    cancelEditing();
                                }}
                                disabled={activeSaving || !!destructiveAction}
                            >
                                Cancel
                            </RecordCardActionButton>
                        </>
                    ) : (
                        <SupportWriteGuard>
                            {canEdit && <RecordCardActionButton onClick={startEditing}>Edit</RecordCardActionButton>}
                        </SupportWriteGuard>
                    )}
                </div>
            </div>
        </div>
    );
}

// BL-177 Step 2m: the client's communication choices are THEIRS. Staff see them read-only, and can record an opt-out;
// a support admin can change them for the client with a reason. Nothing here writes the contacts table.
function SmsConsentProfile({ contact, onSaved }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    const loadHistory = useCallback(async () => {
        const { data, error: historyError } = await supabaseClient
            .from('contact_sms_consent_history')
            .select('id, transactional_consent, marketing_consent, source, consent_at, consent_text, customer_agreed_verbally, communication_preferences, consent_note, created_at')
            .eq('contact_id', contact.id)
            .order('created_at', { ascending: false })
            .limit(10);
        if (historyError) setMessage('Consent history could not be loaded.');
        else setHistory(data || []);
        setLoading(false);
    }, [contact.id]);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    return (
        <section className="mt-5 rounded-lg border border-[var(--surface-border)] p-4" aria-label="Communications">
            <ContactCommunications
                contact={contact}
                heading="Preferences · Communications"
                idPrefix={`profile-preferences-${contact.id}`}
                onChanged={async () => { await Promise.all([onSaved?.(), loadHistory()]); }}
            />
            <p className="mt-2 text-xs text-[var(--ink-muted)]">
                Last change: {contact.sms_consent_at
                    ? `${consentSourceLabel(contact.sms_consent_source) || 'unknown source'} · ${new Date(contact.sms_consent_at).toLocaleString()}`
                    : 'No recorded consent change.'}
            </p>
            {message && <p className="mt-2 text-xs text-[var(--ink)]" role="status">{message}</p>}
            <div className="mt-3">
                <h4 className="text-xs font-medium text-[var(--ink)]">History</h4>
                {loading ? <p className="mt-1 text-xs text-[var(--ink-muted)]">Loading…</p> : history.length === 0
                    ? <p className="mt-1 text-xs text-[var(--ink-muted)]">No communication preference changes recorded.</p>
                    : (
                        <table className="mt-1 w-full text-left text-xs text-[var(--ink-muted)]">
                            <thead><tr><th className="py-1">When</th><th>What changed</th><th>How</th></tr></thead>
                            <tbody>{history.map((entry) => (
                                <tr key={entry.id} className="border-t border-[var(--surface-border)] align-top">
                                    <td className="py-1 pr-2">{new Date(entry.consent_at).toLocaleString()}</td>
                                    <td className="py-1 pr-2">{entry.consent_text || 'Communication preferences updated'}</td>
                                    <td className="py-1">{consentSourceLabel(entry.source)}{entry.consent_note ? ` · ${entry.consent_note}` : ''}</td>
                                </tr>
                            ))}</tbody>
                        </table>
                    )}
            </div>
        </section>
    );
}

// Not "coming soon" — a specific answer to "why is this empty?".
function ComingTab({ tab }) {
    return (
        <div className="py-10 text-center">
            <div className="text-sm font-medium text-[var(--ink)]">{tab.label} — not built yet</div>
            <div className="text-xs text-[var(--ink-muted)] mt-2 max-w-md mx-auto leading-relaxed">
                {tab.waiting}
            </div>
        </div>
    );
}

// BL-173 Gate 3c — the staff manual card-entry panel lives here, on the account the card
// belongs to. Everything else this tab will eventually hold is Module 6's, and still unbuilt.
function BillingTab({ account, contact }) {
    return (
        <div className="space-y-5">
            {contact?.id
                ? <ContactBillingPreferences contact={contact} />
                : <p className="text-sm text-[var(--ink-muted)]">Add a primary contact to manage billing preferences.</p>}
            <StaffCardEntryPanel account={account} />
            <AccountInvoicesForCharge account={account} />
            <div className="border-t border-[var(--surface-border)] pt-4 text-xs leading-relaxed text-[var(--ink-muted)]">
                Charges are confirmed by Stripe&apos;s webhook and reflected in the invoice ledger.
            </div>
        </div>
    );
}

function AccountInvoicesForCharge({ account }) {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let active = true;
        async function loadInvoices() {
            setLoading(true);
            setError('');
            const { data, error: loadError } = await supabaseClient
                .from('invoices')
                .select('id, org_id, account_id, invoice_number, status, lifecycle_status, total, subtotal, final_amount, quoted_amount, created_at')
                .eq('account_id', account.id)
                .order('created_at', { ascending: false })
                .limit(20);
            if (!active) return;
            if (loadError) setError(loadError.message || 'Invoices could not be loaded.');
            else setInvoices(data || []);
            setLoading(false);
        }
        loadInvoices();
        return () => { active = false; };
    }, [account.id]);

    const updateInvoice = (updated) => {
        setInvoices((current) => current.map((invoice) => invoice.id === updated.id ? { ...invoice, ...updated } : invoice));
    };

    return (
        <section className="space-y-3" aria-label="Client invoices">
            <h3 className="text-sm font-semibold text-[var(--ink)]">Invoices</h3>
            {loading && <p className="text-sm text-[var(--ink-muted)]">Loading invoices…</p>}
            {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
            {!loading && !error && invoices.length === 0 && (
                <p className="text-sm text-[var(--ink-muted)]">No invoices are recorded for this client.</p>
            )}
            {invoices.map((invoice) => (
                <div key={invoice.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--surface-border)] py-2">
                    <div>
                        <div className="text-sm font-medium text-[var(--ink)]">{invoice.invoice_number || 'Invoice'}</div>
                        <div className="text-xs text-[var(--ink-muted)]">
                            {invoice.status || invoice.lifecycle_status || 'Status unavailable'}
                        </div>
                    </div>
                    <ChargeInvoiceAction invoice={invoice} onInvoiceStatusChange={updateInvoice} />
                </div>
            ))}
        </section>
    );
}

function SubAccountsTab({ subs, loaded }) {    if (!loaded) return <div className="text-sm text-gray-500 py-6 text-center">Loading…</div>;
    if (!subs.length) {
        return (
            <div className="text-sm text-gray-500 py-6 text-center">
                No sub-accounts.
                <div className="text-xs text-gray-600 mt-1">
                    Sub-accounts are compartments under this client — Hansgrohe’s branches, or two people on one household account.
                </div>
            </div>
        );
    }
    return (
        <div className="divide-y divide-[var(--surface-border)]">
            {subs.map(s => (
                <div key={s.id} className="py-2.5 flex items-center gap-3">
                    <span className="w-24 flex-shrink-0 font-mono text-xs text-[var(--ink-muted)]">{s.client_code}</span>
                    <span className="flex-1 truncate text-sm text-[var(--ink)]">{s.sub_account_name}</span>
                    <Chip tone={statusMeta(s.status).tone}>{statusMeta(s.status).label}</Chip>
                </div>
            ))}
        </div>
    );
}

export default ClientFullCard;
